/** Single HTTP entry point. Same-origin, cookie-based (BFF) — no tokens in JS.
 *  Types here are hand-written for now; `npm run gen:api` will replace them with
 *  the generated OpenAPI schema (OUI-07) so backend drift becomes a compile error. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
    /** the server's full `detail` (e.g. `errors: string[]` on validation) */
    readonly detail?: { errors?: string[] } & Record<string, unknown>,
  ) {
    super(message ?? code);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const code = data?.detail?.error_code ?? data?.error_code ?? `HTTP_${res.status}`;
    throw new ApiError(res.status, code, data?.detail?.message, data?.detail);
  }
  return data as T;
}

/** multipart upload of one blob as `file` — the one image-upload path */
export function uploadFile<T>(path: string, blob: Blob): Promise<T> {
  const form = new FormData();
  form.append("file", blob, "image.jpg");
  return fetch(path, { method: "POST", credentials: "same-origin", body: form }).then(async (r) => {
    if (!r.ok) {
      const d = await r.json().catch(() => null);
      throw new ApiError(r.status, d?.detail?.error_code ?? `HTTP_${r.status}`, d?.detail?.message, d?.detail);
    }
    return r.json() as Promise<T>;
  });
}

export const api = {
  get: <T>(p: string) => req<T>(p),
  post: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  /** PATCH is a PARTIAL update — only the fields sent are changed. Used where
   *  the server distinguishes "not supplied" from "set to null", as the
   *  products module does: omitting `archived` leaves it alone, sending
   *  `false` un-archives. PUT cannot express that. */
  patch: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  del: <T>(p: string) => req<T>(p, { method: "DELETE" }),
};

/* ---------------- auth ---------------- */
export interface Me {
  status: string;
  login_id: string;
  role: string;
  company_id: string;
  company_name?: string | null;
  /** The caller belongs to NO company and reads across all of them — the same
   *  fact the server derives with `is_internal(role)` for Scope. Distinct from
   *  a missing `company_name`, which only means the name is unrecorded. */
  cross_company?: boolean;
  granted: number[];
}
export interface SpaceOption {
  company_id: string;
  company_name: string;
  space_id: string;
  space_name: string;
}
export type LoginResult = Me | { status: "space_choice"; spaces: SpaceOption[] };

/** Which identity backend the console talks to.
 *
 *  **ORBIT IS NOW THE DEFAULT** (2026-09-26). Companies and users were migrated
 *  from Fabric, password hashes verbatim so nobody's password changed, and
 *  Fabric's MFA_ENCRYPTION_KEY was moved with them so nobody re-enrolled an
 *  authenticator. Verified by a real sign-in before this flipped.
 *
 *  `?v1=1` FALLS BACK TO FABRIC, and it stays until Fabric is switched off.
 *  It is a client-side escape hatch on purpose: if something here is wrong, it
 *  needs no deploy, no rollback and nobody with ssh — anybody can append it to
 *  the URL and be back on the system that was working an hour ago. The cost of
 *  keeping it is one branch; the cost of not having it is an outage that only
 *  ends when somebody with access is awake.
 *
 *  `?v1=0` returns to Orbit. The choice lives in sessionStorage rather than the
 *  URL because the SPA rewrites the path on every navigation and the flag would
 *  be lost on the first click — and sessionStorage rather than localStorage so
 *  closing the tab returns to the default rather than pinning somebody to the
 *  old system for ever.
 *
 *  Delete this function when Fabric is retired; the base becomes a constant.
 */
export function authBase(): string {
  try {
    const q = new URLSearchParams(window.location.search).get("v1");
    if (q === "1") sessionStorage.setItem("orbit_auth_v1", "1");
    if (q === "0") sessionStorage.removeItem("orbit_auth_v1");
    return sessionStorage.getItem("orbit_auth_v1") === "1" ? "/auth" : "/auth/v2";
  } catch {
    // Private windows and blocked site data both throw. The safe answer is the
    // DEFAULT — the system that holds the migrated users.
    return "/auth/v2";
  }
}

export const usingOrbitIdentity = () => authBase() === "/auth/v2";

export const auth = {
  me: () => api.get<Me>(`${authBase()}/me`),
  login: (body: {
    login_id: string;
    password: string;
    mfa_code?: string;
    company_id?: string;
    space_id?: string;
  }) => api.post<LoginResult>(`${authBase()}/login`, body),
  logout: () => api.post<{ status: string }>(`${authBase()}/logout`),
  /** MFA enrolment, Orbit's own identity only.
   *
   *  `begin` returns a CANDIDATE secret and stores nothing — a secret written
   *  before a code proves it would leave somebody locked out of an account that
   *  believes it has MFA. The caller holds it until `finish` verifies.
   *
   *  The secret is passed BACK to `finish` for that reason; it is not a session
   *  value the server kept. */
  mfaBegin: () => api.post<{ secret: string; otpauth_uri: string; qr_png: string }>(
    `${authBase()}/mfa/begin`, {}),
  mfaFinish: (secret: string, code: string) =>
    api.post<{ status: string; backup_codes: string[] }>(
      `${authBase()}/mfa/finish`, { secret, code }),
  mfaSubmit: (code: string) => api.post<Me>(`${authBase()}/mfa`, { code }),
  /** Pull this session's permission map from Fabric NOW — the Refresh button.
   *  Returns the same shape as /auth/me, so the caller can swap session state
   *  from one response instead of re-fetching. */
  /** Re-read this session's capabilities NOW — the Refresh button.
   *
   *  Routed through `authBase()` like everything else. It was hardcoded to
   *  `/auth/resync`, so once the console logged in through Orbit the button
   *  posted to an endpoint that requires a FABRIC session and answered 401.
   *
   *  Under Orbit's identity there is nothing to resync — `capabilities_for(plan,
   *  role)` is computed per request and cannot go stale — so `/auth/v2/resync`
   *  simply returns the current answer. The button stays honest either way:
   *  against Fabric it forces a fetch, against Orbit it confirms what is
   *  already true. */
  resync: () => api.post<Me>(`${authBase()}/resync`, {}),
};

export interface PlanMatrix {
  plans: { id: string; label: string; limits: { retention_days: number } }[];
  roles: string[];
  your_plan: string | null;   // null = super admin: no company, so no plan
  your_role: string;
  capabilities: { id: number; group: string; label: string; detail: string;
                  grid: Record<string, Record<string, boolean>> }[];
}
export const plans = {
  matrix: () => api.get<PlanMatrix>("/modules/plans"),
};

/* ---------------- link (gate 800) ---------------- */
export interface BrokerStats {
  broker: { kind: string; connected: boolean; error: string | null; supports_client_list: boolean };
  metrics: {
    connected_clients: number | null;
    publish_in: number | null;
    publish_out: number | null;
    publish_bytes_in: number | null;
    publish_bytes_out: number | null;
    msgs_per_min_in: number | null;
    msgs_per_sec_in: number | null;
    packets_in: number | null;
    packets_out: number | null;
    bytes_in: number | null;
    bytes_out: number | null;
    subscriptions: number | null;
    retained: number | null;
    dropped: number | null;
    uptime_s: number | null;
    auth_failures: number | null; // EMQX only -> render "—"
    acl_denials: number | null; // EMQX only -> render "—"
  };
  ingest: {
    stats: Record<string, number>;
    gate_open: boolean | null;
    started_at: string;
    age_s: number;
  } | null;
  note: string;
}

export interface PlatformClient {
  identity: string;
  clientid: string;
  source: "workbench" | "platform";
  kind: string;
  connected: boolean;
  state: string;
  uptime_s: number | null;
  keepalive: number;
  recv_msg: number | null;
  send_msg: number | null;
  subscriptions: string[];
  ip_address: string;
  proto_ver: string;
}
export interface BrokerClient {
  clientid?: string;
  username?: string;
  connected?: boolean;
  connected_at?: string;
  ip_address?: string;
  proto_ver?: string | number;
  recv_msg?: number;
  send_msg?: number;
  subscriptions_cnt?: number;
}
export interface ClientsResponse {
  broker_supported: boolean;
  broker_reason: string | null;
  broker: BrokerClient[];
  platform: PlatformClient[];
}

export interface NodeSub {
  topic: string;
  qos: number;
}
export interface NodeTopicStat {
  topic: string;
  published: number;
  received: number;
  bytes_out: number;
  bytes_in: number;
  last_qos: number | null;
  last_seen: number | null;
}
export interface DebugNode {
  id: string;
  kind: "telematics" | "server";
  identity: string;
  keepalive: number;
  state: "stopped" | "connecting" | "online" | "error" | "killed";
  error: string | null;
  uptime_s: number | null;
  msgs_in: number;
  msgs_out: number;
  subs: NodeSub[];
  topics: NodeTopicStat[];
}
export interface LogLine {
  seq: number;
  ts: number;
  kind: "in" | "out" | "ping" | "conn" | "sub" | "err" | "proto" | "info";
  topic: string;
  text: string;
}
export interface MsgRow {
  seq: number;
  ts: number;
  dir: "in" | "out";
  topic: string;
  qos: number;
  size: number;
  hex: string;
  text: string;
}

export interface PlatformAlert {
  key: string;
  severity: "crit" | "warn";
  message: string;
  first_seen: string;
  last_seen: string;
}

export const link = {
  stats: () => api.get<BrokerStats>("/modules/link/stats"),
  clients: () => api.get<ClientsResponse>("/modules/link/clients"),
  identities: () =>
    api.get<{ enabled: boolean; telematics: string[]; server: string[] }>(
      "/modules/link/identities",
    ),
  nodes: () => api.get<{ enabled: boolean; nodes: DebugNode[] }>("/modules/link/nodes"),
  createNode: (b: { kind: string; identity: string; keepalive: number; subs: NodeSub[] }) =>
    api.post<DebugNode>("/modules/link/nodes", b),
  start: (id: string) => api.post<DebugNode>(`/modules/link/nodes/${id}/start`),
  stop: (id: string) => api.post<DebugNode>(`/modules/link/nodes/${id}/stop`),
  kill: (id: string) => api.post<DebugNode>(`/modules/link/nodes/${id}/stop?kill=true`),
  remove: (id: string) => api.del<{ status: string }>(`/modules/link/nodes/${id}`),
  publish: (id: string, b: { topic: string; payload: string; qos: number; retain: boolean }) =>
    api.post<DebugNode>(`/modules/link/nodes/${id}/publish`, b),
  subscribe: (id: string, b: NodeSub) =>
    api.post<DebugNode>(`/modules/link/nodes/${id}/subscribe`, b),
  unsubscribe: (id: string, topic: string) =>
    api.del<DebugNode>(`/modules/link/nodes/${id}/subscribe?topic=${encodeURIComponent(topic)}`),
  log: (id: string, since: number) =>
    api.get<{ lines: LogLine[] }>(`/modules/link/nodes/${id}/log?since=${since}`),
  messages: (id: string, since: number, filter: string) =>
    api.get<{ messages: MsgRow[] }>(
      `/modules/link/nodes/${id}/messages?since=${since}&filter=${encodeURIComponent(filter)}`,
    ),
  alerts: () => api.get<{ alerts: PlatformAlert[] }>("/modules/link/alerts"),
  clear: (id: string, what: "log" | "messages" | "all") =>
    api.post<{ status: string }>(`/modules/link/nodes/${id}/clear?what=${what}`),
};

// ---- FOTA (Orbit OTA) ----
export interface Firmware {
  id: string; product_id: string; version: number; size: number;
  sha256: string; url_path: string; channel: string; status: string;
  filename: string | null; created_at?: string;
  software: string; version_id: string | null; companies: string[];
}
export interface FwSlice { app_ver: number | null; count: number }
export interface FotaDevice {
  imei: string; mode: string | null; app_ver: number | null; bl_ver: number | null;
  plane_vers: { modem_cell?: { sw?: number }; ble?: { sw?: number; boot?: number } } | null;
  company_id: string | null; company: string | null; last_seen: string | null;
}
export interface FotaMethod {
  key: string; software: string; route: string; path: string; models: number; supported_now: boolean;
}
export interface FotaDeviceMeta {
  companies: { company_id: string; name: string }[];
  // Did the server FILTER this list, or is it the whole platform? One company
  // means different things in the two cases and CompanyFilter needs to know.
  scoped: boolean;
  bl_vers: number[]; app_vers: number[];
  /** How many of `total` a LIVE rollout still holds (0078). They are excluded
   *  from a new batch, so showing `total` alone would promise devices the
   *  rollout will not touch. They rejoin when that rollout succeeds for them,
   *  is cancelled, or expires. */
  held_elsewhere: number;
}
export interface Batch {
  id: string; name: string; firmware_id: string; target_version: number;
  status: string; wave_size: number; created_at?: string; archived_at?: string | null; total: number;
}
export interface BatchDevice {
  connection_id: string; state: string; last_st: number | null; last_err: number | null;
  attempts: number; error_count: number; current_app_ver: number | null; mode: string | null;
  fota_allowed: boolean | null; converged: boolean;
}
export interface SimDev {
  imei: string; mode: string; app_ver: number; bl_ver: number; app_valid: boolean;
  state: string; last_st: number; fail_at: number | null; fota_allowed?: boolean;
  staged_ver?: number | null; log: { ts: number; text: string }[];
}

export const fota = {
  firmware: () => api.get<{ firmware: Firmware[] }>("/modules/fota/firmware"),
  registerFirmware: (form: FormData) =>
    fetch("/modules/fota/firmware", { method: "POST", credentials: "same-origin", body: form })
      .then((r) => r.json() as Promise<{ id: string; version: number; sha256: string; url_path: string }>),
  deleteFirmware: (id: string) => api.del<{ status: string }>(`/modules/fota/firmware/${id}`),
  setFirmwareCompanies: (id: string, company_ids: string[]) =>
    api.put<{ company_ids: string[] }>(`/modules/fota/firmware/${id}/companies`, { company_ids }),
  batches: () => api.get<{ batches: Batch[] }>("/modules/fota/batches"),
  createBatch: (b: {
    firmware_id: string; name: string; connection_ids?: string[]; min_version?: number; wave_size?: number;
    /** How long the command lives, in hours. Omitted = the server default.
     *  There is no "forever": a rollout that never ends holds its unreachable
     *  devices out of every future rollout (0078). */
    lifetime_hours?: number;
    selector?: { company?: string | null; asset_type?: string | null; version_id?: string | null; method?: string | null; bl_vers?: number[] | null; app_vers?: number[] | null; below_version?: number | null };
  }) => api.post<{ id: string; total: number; status: string }>("/modules/fota/batches", b),
  batch: (id: string) =>
    api.get<{ counts: Record<string, number>; devices: BatchDevice[]; total: number }>(
      `/modules/fota/batches/${id}`,
    ),
  startBatch: (id: string) => api.post<{ status: string }>(`/modules/fota/batches/${id}/start`),
  // CANCEL, not stop: it is final, there is no resume (D-16).
  cancelBatch: (id: string) => api.post<{ status: string }>(`/modules/fota/batches/${id}/cancel`),
  archiveBatch: (id: string) => api.post<{ status: string }>(`/modules/fota/batches/${id}/archive`),
  deleteBatch: (id: string) => api.del<{ status: string }>(`/modules/fota/batches/${id}`),
  fwDistribution: () => api.get<{ slices: FwSlice[] }>("/modules/fota/fw-distribution"),
  devices: (f?: { company?: string; state?: string; bl_ver?: string; app_ver?: string; method?: string; version_id?: string; asset_type?: string }) =>
    api.get<{ devices: FotaDevice[]; total: number; meta: FotaDeviceMeta }>(`/modules/fota/devices${qs(f)}`),
  // per-IMEI FOTA debug log (works for real devices + sims)
  watched: () => api.get<{ watched: WatchRow[] }>("/modules/fota/debug/watch"),
  arm: (imei: string, note?: string) =>
    api.post<{ status: string; imei: string }>("/modules/fota/debug/watch", { imei, note }),
  disarm: (imei: string) => api.del<{ status: string }>(`/modules/fota/debug/watch/${imei}`),
  events: (imei: string, after: number) =>
    api.get<{ events: FotaEvent[] }>(`/modules/fota/debug/events/${encodeURIComponent(imei)}?after=${after}`),
  clearEvents: (imei: string) =>
    api.del<{ status: string }>(`/modules/fota/debug/events/${encodeURIComponent(imei)}`),
};
export interface WatchRow { imei: string; added_at: string; note: string | null }
export interface FotaEvent {
  // `warn` is a COMMAND REFUSAL (err 14 no_such_node): the device rejected
  // the command without changing its stage. It is not an error — no rollout
  // moves and no retry budget is spent — but it is not routine either.
  // It was missing here, so refusals rendered with no label and no colour.
  id: number; ts: string; kind: "health" | "command" | "decision" | "warn" | "error";
  st: number | null; stn: string | null; err: number | null; summary: string;
  detail: Record<string, unknown> | null;
}

// ---------------- Live Data (gate: LIVE) ----------------
export interface AssetDataSignal { key: string; label: string; unit: string }
export type AssetDataVal = number | string | null;
export interface AssetDataFwSlice { fw_ver: string | null; count: number }
export interface AssetDataCompanyCount { company_id: string; name: string | null; count: number }
export interface AssetDataOverview {
  total: number; online: number; stale: number; online_window_s: number;
  app_versions: number; freshest: string | null; stalest: string | null;
  by_fw: AssetDataFwSlice[]; by_company: AssetDataCompanyCount[];
  // NOT `internal`: the server has always sent `cross_company`, and this
  // interface said `internal` — so `d.internal` was `undefined` and the
  // per-company breakdown never rendered for the one role allowed to see
  // it. tsc cannot catch it: a wrong name on an interface is consistent
  // with itself. Same shape as the FOTA launch gate (§9k).
  cross_company: boolean;
}
export interface AssetDataCompany { company_id: string; name: string }
export interface AssetDataFindRow {
  imei: string | null;
  /** Set only for an ASSET search; null for a part or connection search.
   *  Asset Control acts on this, not on the IMEI. */
  asset_id?: string | null;
  label: string; sub: string; reason: string | null;
}
export interface AssetDataContext {
  registered: boolean; company?: string | null; in_inventory?: boolean;
  part?: { part_id: string; serial_no: string; kind: string; kind_label: string; model: string; ble_mac: string | null };
  asset?: { asset_id: string; asset_type: string; label: string | null; slot: string };
  driver?: { code: string; name: string; status: string };
}
export interface AssetDataDeviceRow {
  imei: string; company_id: string | null; company: string | null;
  fw_ver: string | null; soc: number | null; last_seen: string | null; online: boolean;
  has_data: boolean;
  registered: boolean;
}
/** WHERE an asset is, and HOW OLD that answer is.
 *
 *  `fix: false` means this is the LAST KNOWN position and the device has since
 *  gone blind — it is not where the asset is now, and the UI must say so.
 *  `ever: false` means no position has ever been reported, which is the only
 *  case with nothing to draw. */
export interface AssetPosition {
  lat: number | null; lng: number | null;
  fix: boolean; ever: boolean;
  at: string | null; age_s: number | null;
}
export interface AssetDataLatest {
  imei: string; company_id: string | null; ts: string | null; seq: number | null;
  last_seen: string | null; gps_fix_ts: string | null; fw_ver: string | null;
  iccid: string | null; online: boolean; values: Record<string, AssetDataVal>;
  board_id: string | null; hw_rev: string | null; vehicle_reg: string | null;
  device_state: string | null; device_type: string | null;
  position: AssetPosition;
}

// ---- the fleet map (D-107): clusters and points arrive TOGETHER ----
export interface FleetMapCell {
  lat: number; lng: number; n: number; live: number;
  by_type: Record<string, number>;
  oldest_fix_age_s: number | null;
}
export interface FleetMapPoint {
  imei: string; company_id: string | null;
  asset_id: string | null; asset_type: string | null; vehicle_reg: string | null;
  lat: number; lng: number;
  /** true = reporting a fix now; false = this is the last known position */
  fix: boolean;
  fix_age_s: number | null;
  last_seen: string | null; seen_age_s: number | null;
  online: boolean; soc: number | null;
}
export interface FleetMapView {
  cells: FleetMapCell[]; points: FleetMapPoint[];
  cell_deg: number; expand_max: number; in_view: number;
  scope: { total: number; located: number; never_located: number };
  online_window_s: number; truncated: boolean; cross_company: boolean;
}
export interface FleetAssetType { asset_type: string; count: number }
export interface AssetDataRecord { cursor: string; ts: string; seq: number; values: Record<string, AssetDataVal> }

export const assetData = {
  overview: (company?: string) =>
    api.get<AssetDataOverview>(`/modules/asset-data/overview${qs({ company })}`),
  companies: () => api.get<{ companies: AssetDataCompany[]; scoped: boolean }>("/modules/asset-data/companies"),
  /** The map is VIEWPORT-BOUNDED: these bounds are not cosmetic, they are what
   *  keeps the read O(view) instead of O(fleet). `s` is the south bound — the
   *  server spells it `s` on the wire and `s_lat` internally, because `s` is
   *  the session there. */
  map: (f: { w: number; s: number; e: number; n: number; zoom: number;
             company?: string; asset_type?: string }) =>
    api.get<FleetMapView>(`/modules/asset-data/map${qs({
      w: String(f.w), s: String(f.s), e: String(f.e), n: String(f.n),
      zoom: String(f.zoom), company: f.company, asset_type: f.asset_type })}`),
  assetTypes: (company?: string) =>
    api.get<{ asset_types: FleetAssetType[] }>(`/modules/asset-data/asset-types${qs({ company })}`),
  devices: (f?: { company?: string; search?: string }) =>
    api.get<{ devices: AssetDataDeviceRow[] }>(`/modules/asset-data/devices${qs(f)}`),
  latest: (imei: string) =>
    api.get<{ latest: AssetDataLatest | null }>(`/modules/asset-data/latest/${encodeURIComponent(imei)}`),
  records: (imei: string, after?: string) =>
    api.get<{ records: AssetDataRecord[]; signals: AssetDataSignal[] }>(
      `/modules/asset-data/records/${encodeURIComponent(imei)}${qs({ after })}`),
  history: (imei: string, f?: { from?: string; to?: string; bucket?: string; limit?: string }) =>
    api.get<{ records: AssetDataRecord[]; signals: AssetDataSignal[]; truncated?: boolean }>(
      `/modules/asset-data/history/${encodeURIComponent(imei)}${qs(f)}`),
  find: (what: "asset" | "part" | "connection", q: string,
         f?: { company?: string; asset_type?: string; kind?: string }) =>
    api.get<{ matches: AssetDataFindRow[] }>(`/modules/asset-data/find${qs({ what, q, ...f })}`),
  context: (ids: string[]) =>
    api.get<{ context: Record<string, AssetDataContext> }>(`/modules/asset-data/context?ids=${ids.map(encodeURIComponent).join(",")}`),
};

// ---------------- Asset Control / Commands (D-110) ----------------
export interface AssetHold {
  level: number; action: number; placed_by: string;
  placed_at: string | null; note: string | null;
}
export interface FleetCommandRow {
  cmd_id: string; level: number | null; action: number; state: string; seq: number;
  connection_id: string; batch_id: string | null;
  created_at: string | null; created_by: string;
  /** how many times Orbit has published it */
  attempts: number;
  /** when Orbit stops retrying delivery — NOT when the restraint lifts. A
   *  hold lives until released; this bounds the publish retries only. */
  expires_at: string | null;
  status_code: number | null; reason_code: number | null;
  levels_after: number | null;
  effective_action: number | null; effective_level: number | null;
  applied_at: string | null;
}
export interface AssetControlView {
  asset_id: string; tenant_id: string; connection_id: string | null;
  /** false = no connected part installed, so the holds stand but nothing can
   *  actuate. The page must not render "immobilised" over an unreachable asset. */
  actuatable: boolean;
  immobile: boolean;
  effective_action: number;
  /** WHOSE type is in force. Required, not cosmetic: the highest open level
   *  decides the type, so a company that asked for WHEELLOCK can be getting
   *  FREEWHEEL while a financier hold stands. */
  effective_level: number | null;
  holds: AssetHold[];
  /** Still able to change — what is outstanding. */
  commands_active: FleetCommandRow[];
  /** Finished, whatever the outcome. Same split FOTA uses for batches. */
  commands_archived: FleetCommandRow[];
  commands: FleetCommandRow[];
  /** FOTA rollouts targeting this asset's device — READ-ONLY (OFL-06e).
   *  Launching stays on the FOTA page; seeing it here explains why a command
   *  behaved oddly ("still staged, waiting for a halt"). */
  fota: FotaEntry[];
  issued?: { cmd_id: string; state: string; publish_error?: string } | null;
  released_a_hold?: boolean;
}

export interface FotaEntry {
  source: "fota"; batch: string; software: string; target_version: number;
  state: string; attempts: number; last_st: number | null; last_err: number | null;
  released_at: string | null; converged_at: string | null;
}
export interface BulkPreview {
  requested: number; targeted: number; skipped: number;
  over_limit: boolean; limit: number;
  assets: { asset_id: string; connection_id: string; already_held_levels: number[] }[];
  skipped_detail: { asset_id: string; reason: string }[];
}
export interface BulkResult {
  batch_id: string; level: number; action: number;
  requested: number; commanded: number;
  skipped: { asset_id: string; reason: string }[];
  failed_to_publish: { asset_id: string; cmd_id: string; publish_error?: string }[];
  results: { asset_id: string; cmd_id: string; state: string }[];
}

export const fleet = {
  asset: (assetId: string) =>
    api.get<AssetControlView>(`/modules/fleet/assets/${encodeURIComponent(assetId)}`),
  immobilize: (assetId: string, body: { action: number; note?: string;
                                        level?: number; lifetime_minutes?: number }) =>
    api.post<AssetControlView>(
      `/modules/fleet/assets/${encodeURIComponent(assetId)}/immobilize`, body),
  mobilize: (assetId: string, level?: number, lifetimeMinutes?: number) =>
    api.post<AssetControlView>(
      `/modules/fleet/assets/${encodeURIComponent(assetId)}/mobilize${qs({
        level: level === undefined ? undefined : String(level),
        lifetime_minutes: lifetimeMinutes === undefined
          ? undefined : String(lifetimeMinutes) })}`, {}),
  /** Prove a second factor. Authorises immobilise for `valid_for_s` — a
   *  mobilise never needs it, so an expired window can't trap a locked bike. */
  stepUp: (code: string) =>
    api.post<{ stepped_up_at: string; valid_for_s: number }>(
      "/modules/fleet/step-up", { code }),
  /** The CSV template an operator fills in. A real server path, because the
   *  browser fetches it directly (tests/ui's byte-fetching guard). */
  /** ONE TEMPLATE PER COMMAND. The sheet is a form, and a form that does not
   *  say what it does is a hazard: a list prepared to release a depot must not
   *  be uploadable into an immobilise run without the operator seeing the word. */
  bulkTemplateUrl: (command = "immobilize") =>
    `/modules/fleet/bulk/template${qs({ command })}`,
  bulkPreview: (assetIds: string[]) =>
    api.post<BulkPreview>("/modules/fleet/bulk/preview", { asset_ids: assetIds }),
  bulkImmobilize: (assetIds: string[], action: number, note?: string,
                   lifetimeMinutes?: number) =>
    api.post<BulkResult>("/modules/fleet/bulk/immobilize",
      { asset_ids: assetIds, action, note, lifetime_minutes: lifetimeMinutes }),
  /** Release THIS LEVEL's hold on many assets. A separate endpoint, not a
   *  flag: it takes no restraint type, and the server deliberately does NOT
   *  gate it on a fresh step-up — freeing vehicles is the recovery action
   *  after a mistaken run, and that is exactly when the window has expired. */
  bulkMobilize: (assetIds: string[], note?: string, lifetimeMinutes?: number) =>
    api.post<BulkResult>("/modules/fleet/bulk/mobilize",
      { asset_ids: assetIds, note, lifetime_minutes: lifetimeMinutes }),
  /** The fleet-wide log. KEYSET, not offset: `before` is the previous page's
   *  `next_before`. An offset would skip rows while the table is written to
   *  underneath an operator paging through it. */
  commands: (p: { before?: string; active?: boolean; limit?: number } = {}) =>
    api.get<CommandLogPage>(`/modules/fleet/commands${qs({
      before: p.before,
      active: p.active ? "true" : undefined,
      limit: p.limit === undefined ? undefined : String(p.limit) })}`),
  /** Call off a command Orbit has not yet delivered. Refused once the device
   *  has it — only a MOBILISE un-arms a device. Cancelling an immobilise
   *  releases that level's hold too, so the two cannot disagree. */
  cancelCommand: (cmdId: string) =>
    api.post<{ cmd_id: string; state: string; released_a_hold: boolean }>(
      `/modules/fleet/commands/${encodeURIComponent(cmdId)}/cancel`, {}),
  /** Recent runs with LIVE outcome counts. */
  batches: (limit?: number) =>
    api.get<{ batches: CommandBatch[] }>(`/modules/fleet/batches${qs({
      limit: limit === undefined ? undefined : String(limit) })}`),
  /** The assets a selector covers: one page plus the full count. The count is
   *  computed in SQL, so a 1,400-asset selection costs what a 14-asset one
   *  does to report. */
  candidates: (sel: CommandSelector, limit = 200, offset = 0) =>
    api.post<{ total: number; assets: CandidateAsset[] }>(
      `/modules/fleet/candidates${qs({ limit: String(limit),
                                       offset: String(offset) })}`, sel),
  /** Create a DRAFT run. Touches no vehicle, places no hold, sends nothing. */
  createBatch: (body: CommandSelector & {
    name?: string; action: number; level?: number;
    lifetime_minutes?: number; wave_size?: number;
  }) => api.post<{ batch_id: string; status: string; requested: number;
                   commanded: number; skipped: { asset_id: string; reason: string }[] }>(
    "/modules/fleet/batches", body),
  /** Counts for the chart, and every device in the run with its state. */
  batch: (batchId: string) =>
    api.get<BatchDetail>(`/modules/fleet/batches/${encodeURIComponent(batchId)}`),
  /** Draft -> active: places the holds and starts delivery. */
  startBatch: (batchId: string) =>
    api.post<{ batch_id: string; status: string }>(
      `/modules/fleet/batches/${encodeURIComponent(batchId)}/start`, {}),
  /** FINAL — there is no resume (D-16). */
  cancelBatch: (batchId: string) =>
    api.post<{ batch_id: string; status: string; cancelled: number }>(
      `/modules/fleet/batches/${encodeURIComponent(batchId)}/cancel`, {}),
  archiveBatch: (batchId: string) =>
    api.post<{ batch_id: string; archived_at: string }>(
      `/modules/fleet/batches/${encodeURIComponent(batchId)}/archive`, {}),
};

export interface CommandLogRow {
  cmd_id: string; asset_id: string | null; tenant_id: string;
  connection_id: string; type: string; expires_at: string | null;
  level: number | null; action: number; state: string; seq: number;
  batch_id: string | null; created_at: string | null; created_by: string;
  status_code: number | null; reason_code: number | null;
  levels_after: number | null;
  effective_action: number | null; effective_level: number | null;
  applied_at: string | null;
}
export interface CommandLogPage {
  commands: CommandLogRow[];
  has_more: boolean;
  /** pass back as `before`; null when there is no further page */
  next_before: string | null;
}
export interface CommandSelector {
  company?: string; asset_type?: string; state?: string;
}
export interface CandidateAsset {
  asset_id: string; tenant_id: string; asset_type: string; state: string;
}
export interface BatchDetail {
  batch_id: string; status: string; type: string; action: number;
  level: number | null; name: string | null; created_by: string;
  created_at: string | null; archived_at: string | null;
  wave_size: number; wave_cursor: number;
  selector: CommandSelector | null;
  /** keyed by command state — this IS the chart */
  counts: Record<string, number>;
  total: number;
  devices: (FleetCommandRow & { asset_id: string | null })[];
}
export interface CommandBatch {
  batch_id: string; tenant_id: string; type: string;
  action: number; level: number | null;
  created_by: string; created_at: string | null;
  /** frozen at run time — what the operator was told when they pressed it */
  requested: number; commanded: number; skipped: number;
  note: string | null;
  by_state: Record<string, number>;
  /** live, derived every read, because these are still changing */
  applied: number; armed: number; refused: number;
  /** how it ENDED when nothing applied — a finished run that reached no
   *  device is not a successful one */
  cancelled: number; superseded: number; expired: number; in_flight: number;
  finished: boolean;
  /** draft | active | done | cancelled | expired — FOTA's vocabulary */
  status: string;
  name: string | null;
  wave_size: number; wave_cursor: number;
  archived_at: string | null;
}

// ---------------- Registry (internal) ----------------
export interface RegDevice {
  // tenant_id is NOT nullable: every enrolled connection has an owner (D-83).
  imei: string; state: string; tenant_id: string; board_id: string | null;
  iccid: string | null; app_ver: number | null; mode: string | null; last_seen: string | null;
}
export interface RegStats {
  registered: number; in_use: number; inventory: number; unregistered_seen: number;
  avg_enroll_to_allocate_days: number | null;
  distribution: { company_id: string | null; name: string; count: number }[];
}
export interface RegEvent {
  ts: string | null; event: string; field: string | null; old: string | null;
  new: string | null; source: string; actor: string | null; detail: Record<string, unknown> | null;
}
export interface RegDetail extends RegDevice {
  deployment_seq: number;
  credential: { kind: string; secret: string; issued_at: string } | null;
  events: RegEvent[];
}
export interface RegCompany { company_id: string; name: string }
export interface AllocResult {
  imei?: string; identifier?: string; current?: string | null; company?: string | null;
  action: string; reason?: string; deployment_seq?: number;
  what?: "connection" | "asset" | "part" | "unknown"; parts_moved?: number;
}
function qs(o?: Record<string, string | undefined>): string {
  if (!o) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}
/* ---------------- drivers (DRIVERS.md phase A) ---------------- */
export interface DriverKyc {
  temp_address: string | null; temp_pincode: string | null;
  perm_address: string | null; perm_pincode: string | null;
  dl_number: string | null; dl_verified_at: string | null;
  source: string; updated_by: string | null; updated_at: string;
}
export interface DriverRow {
  driver_id: string;
  code: string;
  name: string;
  msisdn: string;
  status: "invited" | "active" | "blocked" | "expired" | "removed";
  invited_by: string;
  created_at: string;
  verified_at: string | null;
  blocked_at: string | null;
  last_app_seen: string | null;
  sessions?: { created_at: string; last_seen: string; revoked_at: string | null; bound: boolean;
    device_model: string | null; device_os: string | null; app_ver: string | null }[];
  handset?: { model: string | null; name?: string | null; os: string | null; app_ver: string | null;
    ble: string | null; extra?: Record<string, string | number | boolean> | null; seen: string } | null;
  phone_health?: ({ at: string } & Record<string, unknown>) | null;
  phone_events?: { at: string; type: string; data: Record<string, unknown> | null }[];
  kyc?: DriverKyc | null;
  photo?: { captured_at: string; captured_by: string } | null;
}
export interface DriverStats {
  total: number; invited: number; active: number; blocked: number; removed: number;
}

export interface InviteBody {
  name: string; msisdn: string;
  temp_address?: string | null; temp_pincode?: string | null;
  perm_address?: string | null; perm_pincode?: string | null;
  dl_number?: string | null;
}

export const drivers = {
  stats: () => api.get<DriverStats>("/modules/drivers/stats"),
  list: (f?: { status?: string; search?: string; limit?: string; offset?: string }) =>
    api.get<{ total: number; drivers: DriverRow[] }>(`/modules/drivers${qs(f)}`),
  detail: (id: string) => api.get<DriverRow>(`/modules/drivers/${id}`),
  invite: (b: InviteBody) => api.post<DriverRow>("/modules/drivers", b),
  bulk: (rows: InviteBody[]) =>
    api.post<{ invited: DriverRow[]; failed: { msisdn: string; name: string; reason: string }[] }>(
      "/modules/drivers/bulk", { rows }),
  lifecycle: (id: string, action: "block" | "unblock" | "remove") =>
    api.post<DriverRow>(`/modules/drivers/${id}/${action}`),
  uploadPhoto: (id: string, blob: Blob) =>
    uploadFile<{ status: string; captured_at: string }>(`/modules/drivers/${id}/photo`, blob),
  photoUrl: (id: string, v?: string) =>
    `/modules/drivers/${id}/photo${v ? `?v=${encodeURIComponent(v)}` : ""}`,
  rename: (id: string, name: string) =>
    api.post<DriverRow>(`/modules/drivers/${id}/rename`, { name }),
  changeNumber: (id: string, msisdn: string) =>
    api.post<DriverRow>(`/modules/drivers/${id}/change-number`, { msisdn }),
  saveKyc: (id: string, b: Partial<Omit<DriverKyc, "dl_verified_at" | "source" | "updated_by" | "updated_at">>) =>
    api.put<{ status: string }>(`/modules/drivers/${id}/kyc`, b),
};

export const registry = {
  stats: () => api.get<RegStats>("/modules/registry/stats"),
  devices: (f?: { company?: string; state?: string; search?: string; limit?: string; offset?: string }) =>
    api.get<{ devices: RegDevice[]; total: number }>(`/modules/registry/devices${qs(f)}`),
  device: (imei: string) => api.get<RegDetail>(`/modules/registry/devices/${encodeURIComponent(imei)}`),
  // company_id is REQUIRED: every enrolled connection has an owner (D-83) and
  // this door is platform-only, where the caller's own company is null.
  enroll: (b: { imei: string; company_id: string; board_id?: string; imsi?: string; iccid?: string }) =>
    api.post<{ imei: string; credential: { kind: string; secret: string } }>("/modules/registry/enroll", b),
  importSheet: (form: FormData) =>
    fetch("/modules/registry/import", { method: "POST", credentials: "same-origin", body: form })
      .then((r) => r.json() as Promise<{ results: { row: number; imei: string; result: string; reason?: string }[] }>),
  // A TRANSCRIPTION FIX, not an identity change: the MAC is a fact the device
  // knows about itself and is hand-entered only until the telemetry stream
  // carries it. Serial, board id and connection id have no edit door.
  setPartBleMac: (partId: string, b: { ble_mac: string | null; reason?: string }) =>
    api.patch<{ part_id: string; serial_no: string; ble_mac: string | null;
                action: string; from?: string | null }>(
      `/modules/registry/parts/${encodeURIComponent(partId)}/ble-mac`, b),
  companies: () => api.get<{ companies: RegCompany[]; scoped: boolean }>("/modules/registry/companies"),
  // from_company_id is REQUIRED: this is the pasted-list door, and the per-row
  // may_own check cannot protect the only role that uses it (a super admin has
  // no actor_company, so every row passes). The server refuses the whole batch
  // if any identifier is not currently owned by the stated source.
  transfer: (b: { identifiers: string[]; from_company_id: string; company_id: string; preview?: boolean; ship?: boolean }) =>
    api.post<{ results: AllocResult[] }>("/modules/registry/transfer", b),
  revoke: (imei: string) => api.post<{ status: string }>(`/modules/registry/devices/${encodeURIComponent(imei)}/revoke`),
  templateUrl: "/modules/registry/template",
  transferTemplateUrl: (what?: string) => `/modules/registry/transfer-template${what ? `?what=${what}` : ""}`,
  // ---- parts + assets (REGISTRY.md §9) ----
  partsStats: () => api.get<PartsStats>("/modules/registry/parts/stats"),
  // `company` is the OWNER; `custodian` is who HOLDS it (Ship's question) and
  // `incoming_to` is the D-82 receive inbox — despatched to that company and
  // not yet accepted. All three narrow INSIDE the session scope.
  parts: (f?: { kind?: string; state?: string; company?: string; custodian?: string; incoming_to?: string; in_transit?: string; asset_id?: string; search?: string; limit?: string }) =>
    api.get<{ total: number; parts: RegPart[] }>(`/modules/registry/parts${qs(f)}`),
  part: (id: string) => api.get<RegPartDetail>(`/modules/registry/parts/${id}`),
  // company_id is REQUIRED here even though the server would default it to the
  // caller's own company: for a super admin that default is the session's
  // company CONTEXT, which is not an ownership claim. Making the console state
  // the owner explicitly is the whole point of the picker.
  enrollParts: (rows: PartRowIn[], companyId: string) =>
    api.post<{ enrolled: EnrolledPart[] }>("/modules/registry/parts/enroll", { rows, company_id: companyId }),
  importParts: (form: FormData) =>
    fetch("/modules/registry/parts/import", { method: "POST", credentials: "same-origin", body: form }).then(async (r) => {
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new ApiError(r.status, d?.detail?.error_code ?? `HTTP_${r.status}`, d?.detail?.message, d?.detail);
      return d as { enrolled: { part_id: string; serial_no: string }[] };
    }),
  installPart: (id: string, b: { asset_id: string; slot: string }) => api.post<{ status: string; warning: string | null }>(`/modules/registry/parts/${id}/install`, b),
  // The third write door (REGISTRY.md §10): transfer moves OWNER, ship moves
  // CUSTODIAN, this moves STATE. Installed parts follow their asset (R-O3),
  // so the asset call reports how many came with it.
  // The three custody doors (REGISTRY.md §10, D-82). Shipping RECORDS intent
  // and hands nothing over; the receiver accepts; the sender may call it off.
  ship: (b: { asset_ids?: string[]; part_ids?: string[]; to_company: string }) =>
    api.post<{ results: ShipResult[] }>("/modules/registry/ship", b),
  receive: (b: { asset_ids?: string[]; part_ids?: string[] }) =>
    api.post<{ results: ShipResult[] }>("/modules/registry/receive", b),
  cancelShipment: (b: { asset_ids?: string[]; part_ids?: string[] }) =>
    api.post<{ results: ShipResult[] }>("/modules/registry/ship/cancel", b),
  setAssetState: (id: string, b: { state: string; reason?: string }) =>
    api.post<{ asset_id: string; state: string; action: string; from?: string; parts_moved: number }>(
      `/modules/registry/assets/${encodeURIComponent(id)}/state`, b),
  setPartState: (id: string, b: { state: string; reason?: string }) =>
    api.post<{ part_id: string; serial_no: string; state: string; action: string; from?: string }>(
      `/modules/registry/parts/${id}/state`, b),
  removePart: (id: string, reason: string) => api.post<{ status: string }>(`/modules/registry/parts/${id}/remove`, { reason }),
  transferParts: (b: { part_ids: string[]; company_id: string; ship?: boolean }) => api.post<{ results: { part_id: string; serial_no?: string; action: string; reason?: string }[] }>("/modules/registry/parts/transfer", b),
  partsTemplateUrl: (kind?: string) => `/modules/registry/parts/template${kind ? `?kind=${kind}` : ""}`,
  assets: (f?: { asset_type?: string; company?: string; custodian?: string; incoming_to?: string; in_transit?: string; search?: string; limit?: string }) =>
    api.get<{ total: number; assets: RegAsset[] }>(`/modules/registry/assets${qs(f)}`),
  asset: (id: string) => api.get<RegAssetDetail>(`/modules/registry/assets/${encodeURIComponent(id)}`),
  enrollAsset: (b: { asset_id: string; version_id: string; label?: string; company_id?: string | null }) => api.post<RegAsset>("/modules/registry/assets/enroll", b),
  enrollAssets: (rows: AssetRowIn[], companyId: string) =>
    api.post<{ enrolled: { asset_id: string; asset_type: string; label: string | null }[] }>("/modules/registry/assets/enroll-bulk", { rows, company_id: companyId }),
  buildAsset: (b: { asset_id: string; version_id: string; label?: string | null; company_id?: string | null; parts: { slot: string; serial_no: string }[] }) =>
    api.post<{ asset_id: string; installed: { slot: string; serial_no: string }[]; warnings: string[] }>("/modules/registry/assets/build", b),
  assetsTemplateUrl: (kind?: string) => `/modules/registry/assets/template${kind ? `?kind=${kind}` : ""}`,
  assetDriver: (id: string) => api.get<AssetDriverInfo>(`/modules/registry/assets/${encodeURIComponent(id)}/driver`),
  setAssetDriver: (id: string, driver: string, lease_days?: number | null) => api.post<{ asset_id: string; driver: string; driver_name?: string; action: string; lease_days?: number | null }>(`/modules/registry/assets/${encodeURIComponent(id)}/driver`, { driver, lease_days: lease_days ?? null }),
  releaseAssetDriver: (id: string) => api.del<{ asset_id: string; action: string }>(`/modules/registry/assets/${encodeURIComponent(id)}/driver`),
  transferAssets: (b: { asset_ids: string[]; company_id: string; ship?: boolean }) => api.post<{ results: { asset_id: string; action: string; parts_moved?: number; reason?: string }[] }>("/modules/registry/assets/transfer", b),
  driverAssets: (driverId: string) => api.get<{ assets: { asset_id: string; asset_type: string; label: string | null; since: string }[] }>(`/modules/registry/drivers/${driverId}/assets`),

  /* Members: who MAY drive this asset (D-75). Distinct from the HOLDER above —
     several drivers share a vehicle across shifts, exactly one holds the key. */
  assetMembers: (id: string) => api.get<{ members: AssetMember[] }>(
    `/modules/registry/assets/${encodeURIComponent(id)}/members`),
  addAssetMember: (id: string, driver: string) => api.post<AssetMemberResult>(
    `/modules/registry/assets/${encodeURIComponent(id)}/members`, { driver }),
  removeAssetMember: (id: string, driver: string) => api.del<AssetMemberResult>(
    `/modules/registry/assets/${encodeURIComponent(id)}/members/${encodeURIComponent(driver)}`),
};

export interface AssetMember {
  driver_id: string;
  code: string;
  name: string;
  msisdn: string;
  status: string;
  since: string;
  is_holder: boolean;   // at most one member, and possibly none
}

export interface AssetMemberResult {
  asset_id: string;
  driver: string;
  driver_name?: string;
  action: "added" | "removed" | "noop";
  members?: number;
  key_taken_back?: boolean;   // removing the holder takes the key back too
}
export interface PartsStats {
  parts: number; installed: number;
  inventory: number; deployed: number; repair: number; scrapped: number;
  assets: number; assets_inventory: number; assets_deployed: number;
  assets_repair: number; assets_scrapped: number;
}
export interface EnrolledPart { part_id: string; serial_no: string; kind: string; connection_id: string | null; ble_mac: string | null; credential: { kind: string; secret: string } | null }
export interface AssetDriverRow {
  driver_id: string; code: string; name: string; msisdn: string; status: string;
  valid_from: string; valid_to: string | null; lease_days?: number | null;
}
export interface AssetDriverInfo { current: AssetDriverRow | null; history: AssetDriverRow[]; lease_days_default?: number }
export interface AssetRowIn { asset_id: string; version_id: string; label?: string | null }
export interface PartRowIn {
  kind: string; version_id: string; serial_no: string; connection_id?: string | null; ble_mac?: string | null; board_id?: string | null; iccid?: string | null; imsi?: string | null;
  manufactured_at?: string | null; warranty_until?: string | null; lot?: string | null; notes?: string | null;
}
export interface RegPart {
  part_id: string; kind: string; kind_label: string; version_id: string; model: string; model_version: number;
  manufacturer: string; serial_no: string; board_id: string | null; connection_id: string | null; ble_mac: string | null;
  /** inventory | deployed | repair | scrapped. NOT `installed` — that is
   *  `installed_in`, a structural fact that co-exists with any state. */
  state: string;
  tenant_id: string; tenant: string | null;
  custodian_id: string; custodian: string | null;
  /** IN TRANSIT is derived (D-82): a destination means the goods are moving
   *  and the SENDER still holds them. null = settled. The server has always
   *  sent both; the type simply did not declare them. */
  custodian_pending: string | null; shipped_at: string | null;
  installed_in: string | null; slot: string | null;
  manufactured_at: string | null; warranty_until: string | null; lot: string | null; notes: string | null;
  created_at: string; created_by: string;
  conn_state: string | null; app_ver: number | null; last_seen: string | null;
}
export interface RegPartDetail extends RegPart {
  installations: { asset_id: string; slot: string; valid_from: string; valid_to: string | null; reason: string | null; actor: string | null }[];
  connections: { connection_id: string; valid_from: string; valid_to: string | null }[];
  events: { ts: string; event: string; field: string | null; old: string | null; new: string | null; actor: string | null; source: string; detail: Record<string, unknown> | null }[];
}
export interface ShipResult {
  asset_id?: string; part_id?: string; serial_no?: string;
  action: string; reason?: string;
  from?: string; to?: string; was_going_to?: string; parts_moved?: number;
}
export interface RegAsset {
  asset_id: string; asset_type: string; label: string | null;
  tenant_id: string; tenant: string | null;
  custodian_id: string; custodian: string | null;
  /** IN TRANSIT is derived (D-82): a destination means the goods are moving
   *  and the SENDER still holds them. null = settled. */
  custodian_pending: string | null; shipped_at: string | null;
  manufacturer: string | null; state: string;
  version_id: string | null; model: string | null; parts_installed: number; created_at: string;
}
export interface RegAssetSlot {
  slot: string; role: string; allow: string[]; min: number; max: number; expected: string[]; expected_version_ids: string[];
  installed: { part_id: string; serial_no: string; kind: string; model: string; manufacturer: string; connection_id: string | null; since: string }[];
  ok: boolean;
}
export interface RegAssetDetail extends RegAsset {
  slots: RegAssetSlot[];
  history: { part_id: string; slot: string; valid_from: string; valid_to: string | null; reason: string | null; actor: string | null }[];
}

/* ---------------- api activity (audit trail) ---------------- */
export interface ApiActivityRow {
  id: number; ts: string; actor: string; tenant_id: string | null; action: string;
  status: number | null; ip: string | null; ms: number | null;
  err: { error_code?: string; message?: string; errors?: string[] } | null;
}
export const apiActivity = {
  list: (f?: { actor?: string; path?: string; errors_only?: string; limit?: string }) =>
    api.get<{ rows: ApiActivityRow[] }>(`/modules/audit/activity${qs(f)}`),
};

/* ---------------- asset catalog (ASSET_CATALOG.md) ---------------- */
export interface CatalogField {
  k: string; label: string; type: "num" | "text" | "select" | "bool" | "derived";
  unit?: string; req?: boolean; opts?: string[]; ph?: string; help?: string; from?: string; step?: number;
}
export interface CatalogKind { id: string; label: string; abbr?: string; group: string; asset?: boolean; conn?: boolean }
export interface CatalogSlot {
  slot: string; role: string; allow: string[]; min: number; max: number; qty?: boolean; help?: string;
}
export interface CatalogTemplates {
  kinds: CatalogKind[];
  templates: Record<string, { group: string; fields: CatalogField[] }[]>;
  slots: Record<string, CatalogSlot[]>;
}
export interface Manufacturer {
  manufacturer_id: string; name: string; abbr: string | null; country: string | null; supplies: string[];
  website: string | null; tax_id: string | null; support_contact: string | null; notes: string | null;
  status: string; models: number; created_at: string;
  logo: ImageMeta | null;
}
export interface ImageMeta { captured_at: string; captured_by: string; size: number }
export interface CatalogVersionRow {
  version_id: string; kind: string; manufacturer_id: string; manufacturer: string; name: string;
  version: number; status: "draft" | "active" | "retired"; key_specs: string;
  derived: Record<string, unknown>; units: number | null; updated_at: string; updated_by: string;
  image: ImageMeta | null;
  /** Companies that may BUILD WITH this model. EMPTY = every company —
   *  the same convention firmware release scope uses (D-81). */
  companies: string[];
}
export interface CatalogBomItem {
  slot: string; qty: number; version_id: string; kind: string; name: string; version: number;
  status: string; manufacturer: string;
}
export interface CatalogVersionDetail extends CatalogVersionRow {
  specs: Record<string, unknown>; version_note: string | null;
  created_at: string; created_by: string; activated_at: string | null; retired_at: string | null;
  bom: CatalogBomItem[];
  versions: { version_id: string; version: number; status: string; version_note: string | null }[];
  used_in?: { version_id: string; kind: string; name: string; version: number; status: string }[];
}
export interface ModelBody {
  kind: string; manufacturer_id: string; name: string; specs: Record<string, unknown>;
  bom: { slot: string; child_version_id: string; qty: number }[]; version_note?: string | null;
}

const AC = "/modules/asset-catalog";
export const assetCatalog = {
  templates: () => api.get<CatalogTemplates>(`${AC}/templates`),
  stats: () => api.get<{ asset_models: number; subcomponent_models: number; drafts: number;
    units_on_model: number; units_total: number; by_kind: Record<string, number> }>(`${AC}/stats`),
  manufacturers: () => api.get<{ manufacturers: Manufacturer[] }>(`${AC}/manufacturers`),
  // Replace which companies may build with a model. Empty = everyone.
  setModelCompanies: (id: string, company_ids: string[]) =>
    api.put<{ company_ids: string[] }>(`${AC}/models/${id}/companies`, { company_ids }),
  // NO createManufacturer. A company is born in Organisations (D-105).
  updateManufacturer: (id: string, b: Partial<Manufacturer> & { name: string }) => api.put<Manufacturer>(`${AC}/manufacturers/${id}`, b),
  archiveManufacturer: (id: string) => api.post<Manufacturer>(`${AC}/manufacturers/${id}/archive`),
  models: (f?: { kind?: string; status?: string; manufacturer_id?: string; search?: string; limit?: string; offset?: string }) =>
    api.get<{ total: number; models: CatalogVersionRow[] }>(`${AC}/models${qs(f)}`),
  model: (id: string) => api.get<CatalogVersionDetail>(`${AC}/models/${id}`),
  createModel: (b: ModelBody) => api.post<CatalogVersionDetail>(`${AC}/models`, b),
  // `kind` is NOT in DraftIn and StrictIn forbids unknowns, so sending it is a
  // flat 422 — which is what every model edit has been doing. A model's KIND is
  // fixed for the life of its line, so the server is right not to accept it and
  // the client is what was wrong.
  updateDraft: (id: string, b: Partial<ModelBody>) => {
    const { kind: _kind, ...draft } = b;
    return api.put<CatalogVersionDetail>(`${AC}/models/${id}`, draft);
  },
  activate: (id: string) => api.post<CatalogVersionDetail>(`${AC}/models/${id}/activate`),
  retire: (id: string) => api.post<CatalogVersionDetail>(`${AC}/models/${id}/retire`),
  deleteDraft: (id: string) => api.del<{ status: string }>(`${AC}/models/${id}`),
  uploadImage: (id: string, b: Blob) => uploadFile<ImageMeta>(`${AC}/models/${id}/image`, b),
  imageUrl: (id: string, v?: string) => `${AC}/models/${id}/image${v ? `?v=${encodeURIComponent(v)}` : ""}`,
  deleteImage: (id: string) => api.del<{ status: string }>(`${AC}/models/${id}/image`),
  uploadLogo: (id: string, b: Blob) => uploadFile<ImageMeta>(`${AC}/manufacturers/${id}/logo`, b),
  logoUrl: (id: string, v?: string) => `${AC}/manufacturers/${id}/logo${v ? `?v=${encodeURIComponent(v)}` : ""}`,
  deleteLogo: (id: string) => api.del<{ status: string }>(`${AC}/manufacturers/${id}/logo`),
};

// ---------------------------------------------------------------- products --
// A "product" IS a catalogue kind (D-92). `0062` deleted Fabric's `products`
// table; these read `catalog_kinds` and what hangs off it. The console keeps
// the word "Products" because that is what the team says.
//
// Until now these pages rendered `fixtures.ts`, which is why "Orbit Platform"
// and "Nexcell line 2" were still on screen after the database had stopped
// containing either.

export interface ProductRow {
  kind: string; name: string; abbr: string; group: string;
  is_asset: boolean; is_connected: boolean;
  /** Does this product HAVE software (D-95)? Gates FOTA, Reference files
   *  and Tool updates. NOT the permission map — that is about what a person
   *  may DO in a space and needs no code to run. */
  has_software: boolean;
  status: string;
  spaces: number; permission_maps: number; companies: number;
  reference_files: number; tool_releases: number; catalogue_versions: number;
}
export interface SpaceRow {
  space_id: string; slug: string; name: string; description: string | null;
  archived: boolean; allocations: number; permission_defaults: number;
}
// `perm_uid` and `group_uid` are REQUIRED, and that is the fix for a bug the
// types themselves caused (the §9p defect). They were optional here while the server did not
// send them; every caller wrote `perm.perm_uid ?? ""`, both sides type-checked,
// and every authoring control on the permission map built a URL ending in an
// empty segment. Optional is the right shape only for a field the server may
// legitimately omit - it is the wrong shape for one it forgot.
export interface PermRow { permission_id: number; perm_uid: string; name: string; deprecated: boolean }
export interface PermGroupRow { group_id: number; group_uid: string; name: string; deprecated: boolean; permissions: PermRow[] }
export interface PermMapRow { map_id: string; tool: string; name: string; groups: PermGroupRow[]; permission_count: number }
/** The same vocabulary, with what THIS space grants marked on it (D-102).
 *  `default` is never absent: absence means OFF, and the server states it. */
export interface SpacePermRow extends PermRow { default: boolean }
export interface SpacePermGroupRow extends Omit<PermGroupRow, "permissions"> { permissions: SpacePermRow[] }
export interface SpacePermMapRow extends Omit<PermMapRow, "groups"> { groups: SpacePermGroupRow[] }
/** One permission, after the chain and the two gates (D-102).
 *  `granted` is what the chain says; `effective` is that after entitlement and
 *  the enabled switch. Both, because "the bench got nothing" and "this
 *  permission is off" are different answers and the screen must say which. */
export interface EffectivePerm {
  perm_uid: string; permission_id: number; name: string; deprecated: boolean;
  granted: boolean; effective: boolean; from: "space" | "company" | "user";
}
export interface EffectivePermissions {
  cps_id: string; company_id: string; kind: string; space_id: string; space: string;
  user_id: string | null;
  holds_product: boolean; enabled: boolean; has_access: boolean;
  blocked_because: string | null;
  permissions: EffectivePerm[]; effective_ids: number[];
}
export interface SpacePermissions {
  kind: string; space_id: string; slug: string; name: string; maps: SpacePermMapRow[];
}
export interface RefFileRow {
  file_id: string; filename: string; size_bytes: number; version: number;
  created_at: string | null; company_id?: string | null;
  is_override: boolean; scope: string; archived: boolean;
  /** What the UDS tool fetches (FABRIC_API.md §4). Opaque to the client. */
  download_url: string;
}
/** One field in a product's spec template. `k` is the KEY a model's specs
 *  are stored under, so it is permanent once anything has been saved. */
export interface SpecField {
  k: string; label: string;
  /** `derived` is COMPUTED on read and never stored (IP rating, kWh,
   *  chemistry). It was missing here, in the server's FIELD_TYPES, and
   *  nowhere else — a THIRD copy of one list, each correct on its own. Seven
   *  products carry a derived field and none of them could save their spec.
   *  The editor shows it as "Computed", locked, and never offers it: the
   *  computation lives in code, so declaring one would compute nothing. */
  type: "text" | "num" | "select" | "bool" | "date" | "derived";
  req?: boolean; opts?: string[]; unit?: string; help?: string; ph?: string;
}
export interface SpecGroup { group: string; fields: SpecField[] }

export interface ProductModelRow {
  model_id: string; name: string; status: string; versions: number;
}
export interface RefSlotRow {
  ref_type: string;
  /** the newest LIVE common file */
  current: RefFileRow | null;
  overrides: RefFileRow[];
  /** superseded versions, newest first — the rollback path, which only
   *  exists because uploads never overwrite (D-90) */
  history: RefFileRow[];
}
export interface ToolReleaseRow {
  /** Which models this release serves. EMPTY MEANS EVERY MODEL of the product
   *  (D-90) — the opposite of `firmware_compatibility`, where no rows means
   *  the build reaches nothing. Same shape, opposite default, one join apart;
   *  the reason is blast radius, not taste. */
  models: { model_id: string; name: string }[];
  release_id: string; tool: string; platform: string; version_code: number;
  version_name: string; channel: string; filename: string; size_bytes: number;
  sha256: string; notes: string | null; status: string; created_at: string | null;
}

/** multipart POST of one File plus form fields — reference files and installers. */
function postForm<T>(path: string, file: File, fields: Record<string, string>): Promise<T> {
  const form = new FormData();
  form.append("file", file, file.name);
  for (const [k, v] of Object.entries(fields)) if (v !== "") form.append(k, v);
  return fetch(path, { method: "POST", credentials: "same-origin", body: form }).then(async (r) => {
    const d = await r.json().catch(() => null);
    if (!r.ok) throw new ApiError(r.status, d?.detail?.error_code ?? `HTTP_${r.status}`, d?.detail?.message, d?.detail);
    return d as T;
  });
}

export const products = {
  list: () => api.get<{ products: ProductRow[] }>("/modules/products"),
  spaces: (kind: string) => api.get<{ kind: string; spaces: SpaceRow[] }>(`/modules/products/${kind}/spaces`),
  maps: (kind: string) => api.get<{ kind: string; maps: PermMapRow[] }>(`/modules/products/${kind}/permission-maps`),
  models: (kind: string) => api.get<{ kind: string; models: ProductModelRow[] }>(`/modules/products/${kind}/models`),
  // `model_id` omitted = the product's COMMON files. Not a wildcard: asking
  // for common must not sweep up every model's overrides.
  refFiles: (kind: string, modelId?: string, includeArchived = false) => {
    const qs = new URLSearchParams();
    if (modelId) qs.set("model_id", modelId);
    if (includeArchived) qs.set("include_archived", "true");
    const s = qs.toString();
    return api.get<{ kind: string; model_id: string | null; slots: RefSlotRow[] }>(
      `/modules/products/${kind}/reference-files${s ? `?${s}` : ""}`);
  },
  releases: (kind: string) => api.get<{ kind: string; releases: ToolReleaseRow[] }>(`/modules/products/${kind}/tool-releases`),
  spacePermissions: (kind: string, spaceId: string) =>
    api.get<SpacePermissions>(`/modules/products/${kind}/spaces/${spaceId}/permissions`),

  // --- authoring: SUPER ADMIN only (MANAGE_PRODUCTS). A company_admin gets
  //     403 from the server; the console also hides the controls, but the
  //     server is what enforces it.
  addSpace: (kind: string, b: { slug: string; name: string; description?: string | null }) =>
    api.post<SpaceRow>(`/modules/products/${kind}/spaces`, b),
  updateSpace: (kind: string, id: string, b: { name?: string; description?: string | null; archived?: boolean }) =>
    api.patch<SpaceRow>(`/modules/products/${kind}/spaces/${id}`, b),
  addMap: (kind: string, b: { tool: string; name?: string }) =>
    api.post<PermMapRow>(`/modules/products/${kind}/permission-maps`, b),
  addGroup: (kind: string, mapId: string, b: { group_id: number; name: string }) =>
    api.post<PermGroupRow>(`/modules/products/${kind}/permission-maps/${mapId}/groups`, b),
  addPermission: (kind: string, groupUid: string, b: { permission_id: number; name: string }) =>
    api.post<PermRow>(`/modules/products/${kind}/permission-groups/${groupUid}/permissions`, b),
  // RENAME ONLY. The tool slug and the numbers are the keys a deployed bench
  // asks by; the server refuses a change to either, with a reason.
  // THE WHOLE MAP IN ONE CALL, Fabric's shape. A row absent from `groups` is
  // DEPRECATED, never deleted — deployed benches hold these numbers and three
  // tables point at them.
  saveMap: (kind: string, mapId: string, b: {
    name: string;
    groups: { group_id: number; name: string;
              permissions: { permission_id: number; name: string }[] }[];
  }) => api.put<{ map: PermMapRow; added: number; deprecated: number }>(
    `/modules/products/${kind}/permission-maps/${mapId}`, b),
  updateMap: (kind: string, mapId: string, b: { name: string }) =>
    api.patch<{ map_id: string; tool: string; name: string }>(
      `/modules/products/${kind}/permission-maps/${mapId}`, b),
  updateGroup: (kind: string, groupUid: string, b: { name?: string; deprecated?: boolean }) =>
    api.patch<{ group_uid: string; group_id: number; name: string;
                deprecated: boolean; permissions_deprecated: number }>(
      `/modules/products/${kind}/permission-groups/${groupUid}`, b),
  updatePermission: (kind: string, permUid: string, b: { name?: string; deprecated?: boolean }) =>
    api.patch<PermRow>(`/modules/products/${kind}/permissions/${permUid}`, b),
  // REPLACES the set. Every permission the caller wants ON must be present -
  // one left out is turned OFF, which is the only way this screen can revoke.
  setSpacePermissions: (kind: string, spaceId: string, defaults: Record<string, boolean>) =>
    api.put<SpacePermissions>(
      `/modules/products/${kind}/spaces/${spaceId}/permissions`, { defaults }),
  // Disable, never delete. Removing the row cascades who may enter the space
  // and every override, and re-allocating returns an empty shell that looks
  // restored.
  // WHICH COMPANIES HOLD ONE SPACE. The counterpart of the list that used to
  // live on the organisation page: the space belongs to the PRODUCT (D-102),
  // so the allocation is managed from the product's side (D-105).
  spaceAllocations: (kind: string, spaceId: string) =>
    api.get<{ kind: string; space_id: string; space: string; space_slug: string;
              allocations: SpaceAllocationRow[] }>(
      `/modules/products/${kind}/spaces/${spaceId}/allocations`),
  effectivePermissions: (cpsId: string, userId?: string) =>
    api.get<EffectivePermissions>(
      `/modules/products/allocations/${cpsId}/permissions${userId ? `?user_id=${encodeURIComponent(userId)}` : ""}`),
  setAllocation: (cpsId: string, enabled: boolean) =>
    api.patch<{ cps_id: string; enabled: boolean }>(
      `/modules/products/allocations/${cpsId}`, { enabled }),
  // ARCHIVE IS NOT DELETE. Every upload is a new version (D-90), so archiving
  // the current file makes the PREVIOUS one what a bench resolves to — the
  // history is the rollback path. Delete refuses a live file.
  setRefFileArchived: (kind: string, fileId: string, archived: boolean) =>
    api.patch<{ file_id: string; filename: string; archived: boolean }>(
      `/modules/products/${kind}/reference-files/${fileId}`, { archived }),
  deleteRefFile: (kind: string, fileId: string) =>
    api.del<{ file_id: string; deleted: boolean }>(
      `/modules/products/${kind}/reference-files/${fileId}`),
  refFileHref: (kind: string, fileId: string) =>
    `/modules/products/${kind}/reference-files/${fileId}/download`,
  uploadRefFile: (kind: string, file: File, ref_type: string,
                  opts: { company_id?: string; model_id?: string } = {}) =>
    postForm<RefFileRow>(`/modules/products/${kind}/reference-files`, file,
      { ref_type, company_id: opts.company_id ?? "", model_id: opts.model_id ?? "" }),
  create: (b: {
    kind: string; label: string; abbr: string; group: string;
    is_asset?: boolean; is_connected?: boolean;
    /** Gates FOTA, Reference files and Tool updates, AND seeds the FOTA
     *  field group SERVER-SIDE from FOTA_METHODS. The console never names
     *  `fota_host_ble` — the keys are a contract `derive()` reads, and a
     *  near-miss saves fine and derives nothing. */
    has_software?: boolean;
  }) => api.post<ProductRow & { spec: SpecGroup[]; bom_slots: unknown[] }>(
    "/modules/products", b),
  spec: (kind: string) => api.get<ProductRow & {
    spec: SpecGroup[]; bom_slots: unknown[];
  }>(`/modules/products/${kind}/spec`),
  setSpec: (kind: string, spec: SpecGroup[]) =>
    api.put<ProductRow & { spec: SpecGroup[] }>(
      `/modules/products/${kind}/spec`, { spec }),
  companies: (kind: string) => api.get<{
    kind: string;
    companies: { company_id: string; name: string; holds_product: boolean }[];
    models: {
      model_id: string; model: string; version_id: string; version: number;
      status: string;
      /** EMPTY = visible to EVERYONE (D-81), not to nobody. */
      visible_to: string[];
    }[];
  }>(`/modules/products/${kind}/companies`),
  setCompanies: (kind: string, company_ids: string[]) =>
    api.put<{ kind: string; company_ids: string[] }>(
      `/modules/products/${kind}/companies`, { company_ids }),
  setModelVisibility: (versionId: string, company_ids: string[]) =>
    api.put<{ company_ids: string[] }>(
      `/modules/asset-catalog/models/${versionId}/companies`, { company_ids }),
  setReleaseModels: (kind: string, id: string, model_ids: string[]) =>
    api.put<{ release_id: string; model_ids: string[]; targets: string }>(
      `/modules/products/${kind}/tool-releases/${id}/models`, { model_ids }),
  uploadRelease: (kind: string, file: File, b: {
    tool_slug: string; version_code: number; version_name: string;
    platform?: string; channel?: string; notes?: string;
  }) => postForm<ToolReleaseRow>(`/modules/products/${kind}/tool-releases`, file, {
    tool_slug: b.tool_slug, version_code: String(b.version_code),
    version_name: b.version_name, platform: b.platform ?? "windows",
    channel: b.channel ?? "stable", notes: b.notes ?? "",
  }),
  setReleaseStatus: (kind: string, id: string, status: string) =>
    api.patch<{ release_id: string; status: string }>(`/modules/products/${kind}/tool-releases/${id}`, { status }),
};

// ----------------------------------------------------------- organisations --
// Dattatray: "Batterysmart CMP-004 … Nexcell Pack Works CMP-011 … Why there
// are these organisations?" Because there was no module: all eighteen were
// invented in fixtures.ts. The database holds three.
//
// An organisation IS a party (D-80), and `is_manufacturer` says which
// organisations also wear the maker facet — which is what lets the catalogue
// pick from here instead of from a second list of the same companies (D-93).

export interface OrganisationRow {
  organisation_id: string; name: string; status: string; plan: string;
  is_manufacturer: boolean; entitlements: string[]; users: number;
  fabric_company_id: string | null;
}
/** One company's hold on one space, from the PRODUCT side (D-105). */
export interface SpaceAllocationRow {
  cps_id: string; company_id: string; company: string;
  custom_name: string | null; enabled: boolean;
  /** false once the entitlement was taken away. The allocation SURVIVES that,
   *  deliberately, so the row has to say when it grants nothing. */
  holds_product: boolean;
  users_with_access: number;
}
export interface AllocationRow {
  cps_id: string; kind: string; space_id: string; space: string;
  space_slug: string; custom_name: string | null; enabled: boolean;
  /** false once the entitlement was taken away. The allocation SURVIVES that,
   *  deliberately — deleting it cascades user access and every override — so
   *  the row has to say when it grants nothing. */
  holds_product: boolean;
  users_with_access: number;
}
/** A space this person's organisation holds, flagged with whether they may
 *  enter it. ONE list rather than "granted" and "available" side by side — the
 *  question is "which of these", not "what exists". */
export interface UserSpaceRow {
  cps_id: string; kind: string; space: string; slug: string;
  enabled: boolean; granted: boolean;
}
/** A client tool this person may be SENT. Derived from what their organisation
 *  holds, which spaces they may enter and which releases are published — never
 *  chosen freely. */
export interface UserToolRow {
  kind: string; product: string; tool_slug: string; platform: string;
  version_name: string; filename: string; size_bytes: number; sha256: string;
}
/** A product the company holds, and which models within it they may build
 *  with. NOT spaces — those are a consequence of holding the product (D-102),
 *  not something chosen or edited here. */
export interface HeldProduct {
  kind: string; label: string;
  models: { model: string; version: number }[];
  /** true when no visibility row exists — which means EVERY model, the
   *  opposite of the product list's empty (D-81). Said, not counted. */
  all_models: boolean;
}
export interface OrganisationDetail {
  organisation_id: string; name: string; status: string; plan: string;
  abbr: string | null; country: string | null; deploys: string[];
  products: HeldProduct[];
}
/** What a company DOES. Two independent lists, returned with the full menu so
 *  the editor needs one request and the operator can see the choice. */
export interface Capabilities {
  organisation_id: string; manufacturer_id: string | null;
  manufactures: string[]; deploys: string[];
  products: { kind: string; label: string; group: string }[];
  assets: { kind: string; label: string }[];
}
export interface OrgUserRow {
  user_id: string; login_id: string; name: string;
  organisation_id: string | null; role: string; mfa: boolean;
  status: string; email_verified: boolean;
  space_access: { kind: string; space: string }[];
}

export interface CreatedUser extends OrgUserRow {
  /** The set-your-password link. No password is ever transmitted (SEC-05):
   *  the account lands with none and this is how the person gets in. */
  invite_token: string;
  invite_expires_at: string;
}

export interface ManufacturerFacet {
  organisation_id: string;
  /** null = this organisation is not a manufacturer */
  manufacturer_id: string | null;
  name?: string; abbr: string | null; supplies: string[];
  country: string | null; status?: string; models?: number;
  /** when the logo was captured, or null. The bytes stay in the catalogue's
   *  image store, keyed by manufacturer_id — copying them into a second
   *  store would be the one-thing-in-two-places D-93 removes. */
  logo_at?: string | null;
}

export const organisations = {
  list: () => api.get<{ organisations: OrganisationRow[]; scoped: boolean }>("/modules/organisations"),
  detail: (id: string) => api.get<OrganisationDetail>(`/modules/organisations/${id}`),
  // Both SUPER ADMIN only (MANAGE_ORGANISATIONS). The console hides the
  // controls; the server refuses independently.
  create: (b: { name: string; plan?: string }) =>
    api.post<OrganisationRow>("/modules/organisations", b),
  createUser: (b: { login_id: string; name: string; role: string; organisation_id?: string | null }) =>
    api.post<CreatedUser>("/modules/organisations/users", b),
  // The MAKER facet (D-80/D-93). No `name` — it comes from the party, and
  // accepting one here would recreate the two-places split this removes.
  setIdentity: (cid: string, b: { abbr?: string | null; country?: string | null; plan?: string }) =>
    api.patch<OrganisationDetail>(`/modules/organisations/${cid}`, b),
  capabilities: (cid: string) =>
    api.get<Capabilities>(`/modules/organisations/${cid}/capabilities`),
  // REPLACES both lists. An unticked box means "not this one", or the screen
  // could never revoke.
  setCapabilities: (cid: string, b: { manufactures?: string[]; deploys?: string[] }) =>
    api.put<Capabilities>(`/modules/organisations/${cid}/capabilities`, b),
  manufacturer: (cid: string) =>
    api.get<ManufacturerFacet>(`/modules/organisations/${cid}/manufacturer`),
  setManufacturer: (cid: string, b: {
    abbr?: string | null; supplies?: string[]; country?: string | null;
  }) => api.put<ManufacturerFacet>(`/modules/organisations/${cid}/manufacturer`, b),
  // `"__platform__"` is a UI-side sentinel only — it never reaches the server
  // as a company id. Platform operators have NO organisation, so no value of
  // `company_id` can select them; the server takes a separate boolean.
  // ---- what an admin may do TO a person. All SUPER ADMIN only, and the ones
  // that touch role, status or existence go through the admin FLOOR on the
  // server: it refuses anything that would leave the platform or an
  // organisation with no active admin.
  //
  // NONE of these returns a password or a reset token (SEC-05). An admin who
  // could read the link is an admin who can take over the account.
  // ONE SAVE, not four triggers. A dropdown that writes on change applies a
  // mis-click before you can take it back, and two fields means two writes
  // where the second can fail leaving the first applied.
  updateProfile: (userId: string, b: {
    name: string; login_id: string; role: string; status: string;
  }) => api.patch<OrgUserRow & { email_changed: boolean }>(
    `/modules/organisations/users/${userId}`, b),
  sendPasswordReset: (userId: string) =>
    api.post<{ status: string; to: string; invite: boolean }>(
      `/modules/organisations/users/${userId}/reset-password`, {}),
  resetMfa: (userId: string) =>
    api.post<OrgUserRow>(`/modules/organisations/users/${userId}/reset-mfa`, {}),
  setUserRole: (userId: string, role: string) =>
    api.patch<OrgUserRow>(`/modules/organisations/users/${userId}/role`, { role }),
  setUserStatus: (userId: string, status: string) =>
    api.patch<OrgUserRow>(`/modules/organisations/users/${userId}/status`, { status }),
  // NO setUserEmail. The address is the account's identity — its login, and
  // what the audit trail means by "who" — so it cannot be changed by anyone,
  // including a super admin. The route is gone too; this binding outliving it
  // is what `tests/ui/test_client_paths_are_served` caught.
  deleteUser: (userId: string) =>
    api.del<{ deleted: boolean }>(`/modules/organisations/users/${userId}`),
  userSpaces: (userId: string) =>
    api.get<{ user_id: string; spaces: UserSpaceRow[] }>(
      `/modules/organisations/users/${userId}/spaces`),
  // REPLACES the set: an unticked box means "not this one", or the screen
  // could never revoke.
  setUserSpaces: (userId: string, cps_ids: string[]) =>
    api.put<{ user_id: string; spaces: UserSpaceRow[] }>(
      `/modules/organisations/users/${userId}/spaces`, { cps_ids }),
  userTools: (userId: string) =>
    api.get<{ user_id: string; blocked_because: string | null; tools: UserToolRow[] }>(
      `/modules/organisations/users/${userId}/tools`),
  // WHICH PRODUCT AND WHICH TOOL. Cluster carries `uds_tool` and `eol_tool`
  // (D-89), so sending only the product made the server pick whichever sorted
  // first — the email named one tool and the download delivered the other.
  sendToolLink: (userId: string, kind: string, tool_slug: string) =>
    api.post<{ status: string; to: string; product: string; tool: string; version: string }>(
      `/modules/organisations/users/${userId}/tool-link`, { kind, tool_slug }),
  users: (companyId?: string) => {
    const qs = companyId === "__platform__" ? "?platform_only=true"
      : companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
    return api.get<{ users: OrgUserRow[]; scoped: boolean }>(
      `/modules/organisations/users${qs}`);
  },
};
